import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Alert, Typography, alpha } from '@mui/material';

import PageContainer from '../components/common/PageContainer';
import ViewHeader from '../components/ViewHeader';
import UnderlineTabs from '../components/common/UnderlineTabs';

// Tab components
import OverviewTab from './taxonomy/OverviewTab';
import ProductIntelligenceTab from './taxonomy/ProductIntelligenceTab';
import QualityInspectorTab from './taxonomy/QualityInspectorTab';
import RulesEngineTab from './taxonomy/RulesEngineTab';
import RelationsManagerTab from './taxonomy/RelationsManagerTab';
import CollectionsManagerTab from './taxonomy/CollectionsManagerTab';
import CompatibilityManagerTab from './taxonomy/CompatibilityManagerTab';
import BulkEditorTab from './taxonomy/BulkEditorTab';
import SystemsTagsTab from './taxonomy/SystemsTagsTab';
import RelationPriorsTab from './taxonomy/RelationPriorsTab';

/**
 * Die zehn Reiter in ANZEIGEREIHENFOLGE — die EINE Abbildung zwischen dem
 * stabilen URL-Schluessel (`?tab=quality`) und dem INDEX, den `UnderlineTabs`
 * fuehrt (`value`/`onChange(Number)`, BS11).
 *
 * 🔴 Die Schluessel sind zeichengleich zu den bisherigen Werten
 * (`TaxonomyHub.jsx` vor G14b-3, `:63-136`): ein geteilter Link
 * `?tab=quality` oeffnet weiterhin den Quality-Reiter (Plan G14b §3.4). Nur
 * die FORM der Leiste aendert sich.
 */
const TAB_KEYS = [
  'overview',
  'intelligence',
  'quality',
  'rules',
  'relations',
  'priors',
  'collections',
  'compatibility',
  'bulk',
  'systems',
];

const VORGABE = 'overview';

/**
 * Die Reiterinhalte samt Beschreibung. Die Symbole der alten MUI-Leiste sind
 * mit G14b-3 entfallen (Plan §7 Q1, Orchestrator-Entscheid A, 2026-10-21):
 * `UnderlineTabs` traegt keine `icon`-Prop, und sie zu ergaenzen waere eine
 * Aenderung am geteilten Baustein (Regel 12 + Registry + Jest) — nicht Teil
 * dieser Karte.
 */
const TABS = [
  {
    key: 'overview',
    label: 'Overview',
    component: OverviewTab,
    description: 'Health metrics and quick actions',
  },
  {
    key: 'intelligence',
    label: 'Product Intelligence',
    component: ProductIntelligenceTab,
    description: 'Aggregated taxonomy, relations, and recommendation readiness per product',
  },
  {
    key: 'quality',
    label: 'Quality Inspector',
    component: QualityInspectorTab,
    description: 'Data quality analysis and conflict resolution',
  },
  {
    key: 'rules',
    label: 'Rules Engine',
    component: RulesEngineTab,
    description: 'Configure taxonomy rules',
  },
  {
    key: 'relations',
    label: 'Relations',
    component: RelationsManagerTab,
    description: 'Product recommendations and relationships',
  },
  {
    key: 'priors',
    label: 'Relation Priors',
    component: RelationPriorsTab,
    description: 'Category-level bias for relation matching',
  },
  {
    key: 'collections',
    label: 'Collections',
    component: CollectionsManagerTab,
    description: 'Manage Shopify dynamic collections',
  },
  {
    key: 'compatibility',
    label: 'Compatibility',
    component: CompatibilityManagerTab,
    description: 'Define explicit compatibility rules for accessories and bundles',
  },
  {
    key: 'bulk',
    label: 'Bulk Editor',
    component: BulkEditorTab,
    description: 'Bulk taxonomy operations',
  },
  {
    key: 'systems',
    label: 'Systems & Tags',
    component: SystemsTagsTab,
    description: 'System definitions and tag management',
  },
];

/**
 * Taxonomy & Relations Hub
 * Unified interface for managing taxonomy, relations, data quality, and rules
 *
 * 🔴 Der Reiter lebt in der ADRESSE (`?tab=<key>`, Vorgabe `overview` ohne
 * Parameter) — Plan G14b §2.4 Nr. 1, §3.1:
 *   · ein Wechsel ist ein `push` und ERSETZT die ganze Query
 *     (`setSearchParams({tab})` bzw. `{}`), damit die Listenparameter des
 *     Quality-Reiters nicht in einen anderen Reiter lecken;
 *   · ein UNBEKANNTER Wert wird verworfen (die URL auf die Vorgabe
 *     zurueckgeschrieben, `replace`) und EINMAL gemeldet (N10.4) — kein
 *     stiller Rueckfall mehr.
 */
export default function TaxonomyHub() {
  const [searchParams, setSearchParams] = useSearchParams();

  const roh = searchParams.get('tab');
  const bekannt = roh == null || roh === '' || TAB_KEYS.includes(roh);
  const tab = bekannt && roh ? roh : VORGABE;

  // N10.4: einmal je unbekanntem Wert melden — nicht bei jedem Render.
  const [hinweis, setHinweis] = useState(null);
  const gemeldetFuer = useRef(null);
  useEffect(() => {
    if (roh && !TAB_KEYS.includes(roh)) {
      if (gemeldetFuer.current !== roh) {
        gemeldetFuer.current = roh;
        setHinweis(`Ignored unknown tab value “${roh}”.`);
      }
      // Die URL auf die Vorgabe zurueck (Plan §2.4 Nr. 1) — `replace`, damit
      // der Fehlgriff keinen zweiten Verlaufseintrag erzeugt.
      setSearchParams({}, { replace: true });
    } else if (gemeldetFuer.current !== null) {
      // 🔴 R15-W3 (Nachbesserung 3, 2026-10-31): der Ref merkte sich den
      // unbekannten Wert fuer die LEBENSDAUER der Komponente. Wer im selben
      // Aufenthalt ein zweites Mal `?tab=foo` erreichte, bekam die URL
      // bereinigt, aber KEINE Meldung. Die Meldung gehoert EINEM
      // Bereinigungsvorgang (Hausform `useListQuery.js:215-234`) — sobald die
      // bereinigte Adresse steht, wird der Ref frei und der naechste
      // Fehlgriff meldet wieder.
      gemeldetFuer.current = null;
    }
  }, [roh, setSearchParams]);

  const index = Math.max(0, TAB_KEYS.indexOf(tab));
  const aktuell = useMemo(() => TABS.find((t) => t.key === tab) || TABS[0], [tab]);
  const CurrentTabComponent = aktuell.component;

  const wechsle = (i) => {
    const key = TAB_KEYS[i];
    if (!key) return;
    setHinweis(null);
    // 🔴 R15-B3 (Nachbesserung 3, 2026-10-31): ein Klick auf den BEREITS
    // aktiven Reiter ist KEIN Wechsel. Ohne diese Zeile lief
    // `setSearchParams({tab})` auch dann und ERSETZTE die ganze Query — auf
    // `?tab=quality&quick=critical&q=xyz` waren Suche, Schwere und Typ danach
    // weg, ohne Meldung und ohne Rueckweg. `UnderlineTabs` ruft `onChange`
    // unbedingt (`UnderlineTabs.jsx:480` Klick, `:484` Enter/Space); der
    // Waechter gehoert deshalb hierher, an den Aufrufer — der Baustein bleibt
    // unberuehrt.
    if (i === index) return;
    // Ein Wechsel ersetzt die ganze Query (Plan §2.4 Nr. 1); die Vorgabe steht
    // ohne Parameter in der Adresse.
    setSearchParams(key === VORGABE ? {} : { tab: key });
  };

  return (
    <PageContainer
      width="wide"
      sx={{ minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}
    >
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Taxonomy & Relations Hub"
          subtitle="Unified management of taxonomy, product relations, and data quality"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Taxonomy Hub' },
          ]}
        />
      </Box>

      {hinweis && (
        // 🔴 R16-W5 (Nachbesserung 3, 2026-10-31): `ignoriert-hinweis` ist die
        // Hausmarke fuer einen VERWORFENEN FILTERWERT (`useListQuery`, in
        // `selektoren.js:163`). Auf dieser Seite traegt der Quality-Reiter
        // dieselbe Marke fuer seinen verworfenen Filterwert
        // (`QualityInspectorTab.jsx`) — ein Selektor konnte beide nicht
        // auseinanderhalten. Diese Meldung gilt dem REITER, nicht einem Filter.
        <Alert severity="info" data-testid="unbekannter-reiter-hinweis" sx={{ mb: 2 }}>
          {hinweis}
        </Alert>
      )}

      {/* Reiterleiste und Panel: `withPanel` gibt der Reihe die Panel-Semantik
          (je Reiter eine `id`, `aria-controls` am gewaehlten Reiter, ein
          `role="tabpanel"` um den Inhalt) — Konzept §5.4, BS11. Den Namen
          verlangt der Baustein (er wirft ohne), er kommt aus `ariaLabel`. */}
      <Box data-testid="liste-reiter">
        <UnderlineTabs
          withPanel
          ariaLabel="Taxonomy sections"
          panelIdPrefix="taxonomy"
          tabs={TABS.map((t) => t.label)}
          // 🔴 Zehn Reiter OHNE Zaehler (§5.2: Inhaltsbereich-Reiter tragen
          // keinen) — aber MIT der ausgerichteten Form: nur sie traegt die
          // BREITENRESERVE der Beschriftung (`UnderlineTabs.jsx:549`, der
          // unsichtbare fette Zwilling). Ohne sie aendert der aktive Reiter
          // beim Wechsel seine eigene Breite (600 vs 400) und die Reihe springt
          // (N5.3/R2). 🔴 G14b-3 NB1 (2026-10-30): die Reserve ist am Baustein
          // belegt (Jest T.8, Mechanismus); ihre WIRKUNG im Browser misst der
          // P11-Ersatz seit dieser Runde an der EIGENEN, ungerundeten Breite
          // des geklickten Reiters — die fruehere x-Messung rundete auf ganze
          // Pixel und liess ±1 px zu, konnte eine Verschiebung darunter also
          // nicht sehen. Alle Werte `null` heisst „kein Zaehler"; die Pille
          // selbst haengt an `count != null`.
          counts={TABS.map(() => null)}
          // 🔴 G14b-3 NB1 (2026-10-30): `scrollable` ausdruecklich gesetzt.
          // Ohne die Prop ist `rollt = scrollable ?? groberZeiger`
          // (`UnderlineTabs.jsx:189`) auf einem FEINEN Zeiger false — und dann
          // steht die `overflowX`-Zeile (`:447`) nicht im Baum. Zehn Reiter
          // passen bei 390 px nicht in eine Zeile: gemessen am Zweig `g14b3a`
          // (2026-10-30, 390 × 900) schob die Reihe das Dokument auf **855 px**
          // (`schmale-rasterzellen.spec.ts` S.2/S.3). Mit der Prop ist die
          // Zeile das Rollgefaess, das sie sein soll (Hausform: die frueheren
          // MUI-`Tabs variant="scrollable"`), und der kalte Deep-Link rollt den
          // aktiven Reiter ins Bild (`:273-278`).
          scrollable
          value={index}
          onChange={wechsle}
        >
          {/* Die Beschreibung des aktiven Reiters — sie ist keine Zone der
              Liste, sondern die Erklaerung der Flaeche (Plan §2.4 Nr. 1). */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {aktuell.description}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <CurrentTabComponent />
          </Box>
        </UnderlineTabs>
      </Box>
    </PageContainer>
  );
}
